import { useEffect, useMemo, useRef, useState, type JSX } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import type { PlaceCreate, PlaceDto, PlaceType, PlaceUpdate } from '@/api/types/Place';
import { PLACE_TYPES } from '@/api/types/Place';
import { DialogSubmitOverlay, SubmitButton } from '@/components/loading';
import { ErrorCodes } from '@/constants/errorCodes';
import {
  createPlaceAndInvalidate,
  deletePlaceAndInvalidate,
  invalidatePlaces,
  updatePlaceAndInvalidate,
  type PlaceMutationError,
} from '@/store/placesSlice';
import { useAppDispatch } from '@/store/hooks';

// CHR-06 §4 — the create/edit dialog, fidelity anchor `prototyp.html#s-edit`:
// Dialog `sm`, title „Adresa — {název}" + the short id + close X, body =
// Název | Typ on one `.row2` line, then Adresa, Kontakt and the multiline
// „Poznámka pro dispečery"; footer = Smazat (error OUTLINED, left, edit only)
// · Zrušit · Uložit.
//
// Only Název and Typ are required (`zadani.md §3`): the client's own table has
// a row with no address („volat, navede"). Delete is a SOFT delete behind a
// confirm — the text already copied into historical jízdy stays untouched.

export const NEW_PLACE_ID = '__new__';

const TAP_TARGET_MIN_PX = 44;
// MUI `size="small"` outlined input box height. The Typ toggle group is centred
// inside a cell of exactly this height so its vertical middle lines up with the
// Název input's — the caption that used to sit above the group pushed it a row
// lower than the field it shares the `.row2` line with.
const SMALL_INPUT_HEIGHT_PX = 40;
const TOGGLE_PADDING_X_FACTOR = 1.75;
const DIALOG_FIELD_GAP_FACTOR = 2.5;
const NOTE_ROWS = 3;
const SHORT_ID_LENGTH = 8;

interface FormShape {
  name: string;
  type: PlaceType;
  address: string;
  contact: string;
  note: string;
}

const emptyForm: FormShape = {
  name: '',
  // The client's registry is all vykládky; a new row is one until told
  // otherwise (the migrated 65 rows are `UNLOADING`).
  type: 'UNLOADING',
  address: '',
  contact: '',
  note: '',
};

const dtoToForm = (p: PlaceDto): FormShape => ({
  name: p.name,
  type: p.type,
  address: p.address ?? '',
  contact: p.contact ?? '',
  note: p.note ?? '',
});

// Blank → `null`, which CLEARS the column on the full-replace PUT. `trim()`
// only — never `normalize`: the note's newlines are load-bearing (§8).
const toNullable = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

// The mutation thunks reject with the api's ADR-0010 error code (see
// `placesSlice` — the default serializer would drop it); `.unwrap()` rethrows
// that reject value verbatim.
const errorCodeOf = (e: unknown): string | undefined =>
  (e as PlaceMutationError | undefined)?.code;

export interface PlaceEditorProps {
  // `null` → closed. `NEW_PLACE_ID` → create. Any other string → the id of the
  // place being edited; `existing` MUST then be populated by the parent.
  placeId: string | null;
  existing: PlaceDto | null;
  onClose: () => void;
  onSaved: (saved: PlaceDto, isCreate: boolean) => void;
  onDeleted: () => void;
}

export const PlaceEditor = ({
  placeId,
  existing,
  onClose,
  onSaved,
  onDeleted,
}: PlaceEditorProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('places');
  // The 409 messages are read from the SHARED code-keyed error mirror in
  // `common.json` (ADR-0010) — the places namespace does not restate them.
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();

  const isCreate = placeId === NEW_PLACE_ID;
  const isOpen = placeId !== null;

  const schema = useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(t('validation.nameRequired')),
        type: yup
          .mixed<PlaceType>()
          .oneOf(PLACE_TYPES as PlaceType[])
          .required(),
        address: yup.string().defined(),
        contact: yup.string().defined(),
        note: yup.string().defined(),
      }),
    [t],
  );

  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormShape>({
    defaultValues: emptyForm,
    resolver: yupResolver(schema) as never,
  });

  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // The form is seeded ONCE per opened place, tracked by id — NOT by the
  // `existing` object identity. The parent rebuilds that object on every list
  // refetch (`rows.find(...)`), and the list refetches behind the open dialog
  // (debounced search, `placesSlice` revision). Keying the effect on identity
  // would `reset()` mid-edit and silently discard what the user had typed.
  const seededForRef = useRef<string | null>(null);
  // Title fallback: if the edited row drops out of the current filter mid-edit,
  // `existing` goes null and the header would otherwise re-title to „Adresa — ".
  const seededNameRef = useRef('');

  useEffect(() => {
    if (placeId === null) {
      seededForRef.current = null;
      return;
    }
    if (seededForRef.current === placeId) return;
    // Editing an existing row: wait for the parent to supply it rather than
    // seeding an empty form we would then have to overwrite.
    if (!isCreate && existing === null) return;
    seededForRef.current = placeId;
    seededNameRef.current = isCreate ? '' : (existing?.name ?? '');
    setSaveError(null);
    setShowDeleteConfirm(false);
    reset(isCreate ? emptyForm : existing ? dtoToForm(existing) : emptyForm);
  }, [placeId, isCreate, existing, reset]);

  const onSubmit = async (values: FormShape): Promise<void> => {
    if (submitting) return;
    setSaveError(null);
    setSubmitting(true);
    const common = {
      name: values.name.trim(),
      type: values.type,
      address: toNullable(values.address),
      contact: toNullable(values.contact),
      note: toNullable(values.note),
    };
    try {
      if (isCreate) {
        const payload: PlaceCreate = { ...common };
        const saved = await dispatch(createPlaceAndInvalidate(payload)).unwrap();
        onSaved(saved, true);
      } else if (existing) {
        const payload: PlaceUpdate = { version: existing.version, ...common };
        const saved = await dispatch(
          updatePlaceAndInvalidate({ id: existing.id, payload }),
        ).unwrap();
        onSaved(saved, false);
      } else {
        // The row vanished from the parent's page while the dialog was open (a
        // filter/search change dropped it). There is nothing to PUT against —
        // say so instead of no-op'ing a click on „Uložit".
        setSaveError(t('toast.saveFailed'));
      }
    } catch (e) {
      const code = errorCodeOf(e);
      if (code === ErrorCodes.PLACE_NAME_EXISTS) {
        // Field-level, not a banner: the Název input is what has to change.
        setError('name', {
          type: 'server',
          message: tCommon(`error.${ErrorCodes.PLACE_NAME_EXISTS}`),
        });
      } else if (code === ErrorCodes.OPTIMISTIC_LOCK) {
        // App-standard conflict handling — the axios interceptor already raised
        // the shared warning toast; the dialog stays open and repeats it inline
        // where the user is looking. Invalidating pulls the WINNING row back
        // into the parent's list, so the version this dialog PUTs with is
        // refreshed and a deliberate re-save goes through instead of 409'ing
        // forever (ADR-0032 W3 semantics, user-driven rather than automatic).
        dispatch(invalidatePlaces());
        setSaveError(tCommon(`error.${ErrorCodes.OPTIMISTIC_LOCK}`));
      } else {
        setSaveError(t('toast.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!existing || deleting) return;
    setDeleting(true);
    try {
      await dispatch(
        deletePlaceAndInvalidate({ id: existing.id, version: existing.version }),
      ).unwrap();
      setShowDeleteConfirm(false);
      onDeleted();
    } catch (e) {
      // Same code-aware treatment as the save path — a stale version on DELETE
      // is a conflict, not a generic failure, and the refreshed row makes the
      // retry succeed.
      if (errorCodeOf(e) === ErrorCodes.OPTIMISTIC_LOCK) {
        dispatch(invalidatePlaces());
        setSaveError(tCommon(`error.${ErrorCodes.OPTIMISTIC_LOCK}`));
      } else {
        setSaveError(t('toast.deleteFailed'));
      }
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const titleStr = isCreate
    ? t('editor.titleNew')
    : t('editor.title', { name: existing?.name ?? seededNameRef.current });

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        data-testid="place-editor"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            py: theme.spacing(1.5),
          }}
        >
          <Box component="span" sx={{ fontSize: 17, fontWeight: 600 }}>
            {titleStr}
          </Box>
          {!isCreate && existing ? (
            <Typography
              variant="caption"
              sx={{
                fontFamily: theme.monospaceFontFamily,
                color: theme.palette.text.secondary,
              }}
              data-testid="place-editor-id"
            >
              {t('editor.id', { id: existing.id.slice(0, SHORT_ID_LENGTH) })}
            </Typography>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={onClose}
            aria-label={t('editor.close')}
            sx={{ width: TAP_TARGET_MIN_PX, height: TAP_TARGET_MIN_PX }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={submitting} />
          <Stack
            component="form"
            id="place-editor-form"
            // `required` on the fields is the VISUAL asterisk only — browser
            // constraint validation would swallow the submit event on an empty
            // required input and RHF/Yup would never run. Validation is Yup's
            // job; the browser must stay out of it.
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            spacing={DIALOG_FIELD_GAP_FACTOR}
            sx={{ pt: theme.spacing(1) }}
          >
            {saveError ? (
              <Alert severity="error" data-testid="place-editor-error">
                {saveError}
              </Alert>
            ) : null}

            {/* `.row2` — Název | Typ side by side, stacking below sm. A real
                two-column GRID, exactly like the prototype's
                `grid-template-columns: 1fr 1fr`: a flex row gives a `fullWidth`
                TextField a 100% flex basis, which starves the Typ column and
                pushes the toggle group outside the `sm` dialog paper (caught on
                the real stack — the buttons rendered beyond the modal edge and
                were unclickable). */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                '& > *': { minWidth: 0 },
              }}
            >
              <TextField
                {...register('name')}
                label={t('field.name')}
                required
                fullWidth
                size="small"
                error={Boolean(errors.name)}
                helperText={errors.name?.message ?? ' '}
                inputProps={{ 'data-testid': 'place-name' }}
              />
              {/* No visible „Typ" caption — the group is self-describing
                  (Nakládka/Vykládka/Obojí) and the caption pushed the toggle a
                  row below the Název baseline. The cell is pinned to the small
                  input's box height and centres the 44px-tap-target group in
                  it, so both controls share one vertical centre. The label
                  survives for assistive tech as the group's `aria-label`. */}
              <Box
                sx={{
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  alignSelf: 'start',
                  height: { xs: 'auto', sm: SMALL_INPUT_HEIGHT_PX },
                }}
              >
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      value={field.value}
                      onChange={(_, next: PlaceType | null) => {
                        // Deselect click — the type is mandatory, keep it.
                        if (next === null) return;
                        field.onChange(next);
                      }}
                      aria-label={t('field.type')}
                      sx={{
                        flexWrap: 'wrap',
                        maxWidth: '100%',
                        '& .MuiToggleButton-root': {
                          minHeight: TAP_TARGET_MIN_PX,
                          px: theme.spacing(TOGGLE_PADDING_X_FACTOR),
                          fontSize: 13.5,
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          borderColor: theme.borderStrong,
                          '&.Mui-selected': {
                            bgcolor: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                            '&:hover': { bgcolor: theme.palette.primary.light },
                          },
                        },
                      }}
                    >
                      {PLACE_TYPES.map((type) => (
                        <ToggleButton
                          key={type}
                          value={type}
                          data-testid={`place-type-${type}`}
                        >
                          {t(`type.${type}`)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  )}
                />
              </Box>
            </Box>

            <TextField
              {...register('address')}
              label={t('field.address')}
              fullWidth
              size="small"
              helperText={t('field.addressHint')}
              inputProps={{ 'data-testid': 'place-address' }}
            />

            <TextField
              {...register('contact')}
              label={t('field.contact')}
              fullWidth
              size="small"
              helperText={t('field.contactHint')}
              inputProps={{ 'data-testid': 'place-contact' }}
            />

            <TextField
              {...register('note')}
              label={t('field.note')}
              fullWidth
              size="small"
              multiline
              minRows={NOTE_ROWS}
              helperText={t('field.noteHint')}
              inputProps={{ 'data-testid': 'place-note' }}
            />
          </Stack>
        </DialogContent>

        {/* `.dlg-foot` — Smazat LEFT (err-outlined), Zrušit + Uložit RIGHT. */}
        <DialogActions sx={{ justifyContent: 'space-between', p: theme.spacing(2) }}>
          <Box>
            {!isCreate && existing ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={submitting}
                data-testid="place-delete-button"
                sx={{ minHeight: TAP_TARGET_MIN_PX }}
              >
                {t('action.delete')}
              </Button>
            ) : null}
          </Box>
          <Stack direction="row" gap={1.25}>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={submitting}
              data-testid="place-cancel-button"
              sx={{ minHeight: TAP_TARGET_MIN_PX }}
            >
              {t('action.cancel')}
            </Button>
            <SubmitButton
              type="submit"
              form="place-editor-form"
              variant="contained"
              loading={submitting || isSubmitting}
              data-testid="place-save-button"
              sx={{ minHeight: TAP_TARGET_MIN_PX, minWidth: 120 }}
            >
              {t('action.save')}
            </SubmitButton>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        data-testid="place-delete-confirm"
      >
        <DialogTitle>{t('editor.deleteTitle')}</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={deleting} />
          <Typography variant="body2">
            {t('editor.deleteConfirm', { name: existing?.name ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleting}
            sx={{ minHeight: TAP_TARGET_MIN_PX }}
          >
            {t('editor.deleteConfirmNo')}
          </Button>
          <SubmitButton
            color="error"
            variant="contained"
            loading={deleting}
            onClick={() => void handleDelete()}
            data-testid="place-delete-confirm-yes"
            sx={{ minHeight: TAP_TARGET_MIN_PX }}
          >
            {t('editor.deleteConfirmYes')}
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
